import type { ReactNode } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Store, Bike } from "lucide-react";
import logo from "@/assets/logo.png";
import { EASE } from "./motion";

/**
 * The frame every unauthenticated screen sits in: sign in, register, the three
 * emailed-link pages.
 *
 * They were five separate centred cards, each with its own heading copy and its
 * own spacing, so the panel had no single answer to "where am I?" and the
 * emailed-link pages in particular read as someone else's app. One shell, one
 * mark, one width.
 *
 * On a wide screen it splits: a petrol brand panel on the left carrying the mark
 * and the pitch, the form on the right. Left-aligned throughout rather than
 * centred — a heading centred over a form looks composed only when the form is
 * one field wide, and these grow to three.
 */
export function AuthShell({
  title,
  lede,
  children,
  footer,
  wide,
}: {
  title: string;
  lede?: string;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  return (
    <div className="flex min-h-full bg-paper">
      {/* Brand panel — desktop only. Hidden on a phone, where a full-bleed petrol
          half would push the form below the fold. */}
      <motion.aside
        initial={{ opacity: 0, x: -24 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, ease: EASE }}
        className="bg-brand relative hidden w-[42%] flex-col justify-between overflow-hidden p-10 lg:flex xl:p-14"
      >
        {/* The mark washed into the corner, drifting slowly so the panel is alive
            without competing with the form. */}
        <img
          src={logo}
          alt=""
          aria-hidden
          className="animate-brand-drift pointer-events-none absolute -bottom-16 -right-16 w-72 opacity-15"
        />

        <div className="relative">
          <span className="flex items-center gap-3">
            <img src={logo} alt="" className="h-10 w-auto" />
            <span className="text-xl font-semibold tracking-tight text-white">
              BikerHat <span className="text-white/70">Partner</span>
            </span>
          </span>
        </div>

        <div className="relative max-w-sm">
          <h2 className="text-3xl font-semibold leading-tight tracking-tight text-white xl:text-4xl">
            The dealer&rsquo;s counter for BikerHat.
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-white/70">
            Every listing, every showroom, every member of your team — in one place, and only yours.
          </p>

          {/* Three facts, each named rather than badged. Verification is never
              implied: these describe what the panel does, not what it certifies. */}
          <ul className="mt-8 space-y-3">
            {[
              { icon: Bike, text: "List every bike you have, in under a minute" },
              { icon: Store, text: "Run every showroom from one rail" },
              { icon: ShieldCheck, text: "Your data stays yours — never sold, never shared" },
            ].map((point) => (
              <li key={point.text} className="flex items-center gap-3 text-sm text-white/80">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/10">
                  <point.icon className="h-4.5 w-4.5" aria-hidden />
                </span>
                {point.text}
              </li>
            ))}
          </ul>
        </div>

        <p className="relative text-xs text-white/40">
          BikerHat is a classifieds marketplace. This panel is the seller&rsquo;s side of it.
        </p>
      </motion.aside>

      <div className="flex flex-1 flex-col">
        {/* Compact header on mobile; the brand panel already carries this on desktop. */}
        <header className="flex items-center gap-2.5 border-b border-line bg-raised px-4 py-3 sm:px-6 lg:hidden">
          <img src={logo} alt="" className="h-7 w-auto" />
          <span className="text-base font-semibold tracking-tight">
            <span className="text-ink">BikerHat</span>
            <span className="text-primary"> Partner</span>
          </span>
        </header>

        <main className="flex flex-1 items-start justify-center px-4 py-8 sm:items-center sm:px-6 sm:py-12">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
            className={`w-full ${wide ? "max-w-lg" : "max-w-sm"}`}
          >
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">{title}</h1>
            {lede ? <p className="mt-2 text-sm leading-relaxed text-ink-muted">{lede}</p> : null}

            {/*
              The one surface, with a 3px petrol rule across its head.

              Not a floating rounded card: `.surface` is the same hairline block
              the authenticated screens are built from, so signing in and using
              the panel feel like one product. The petrol rule marks this as the
              one block on the page you act in.
            */}
            <div className="surface mt-6">
              {/* <div aria-hidden className="h-[3px] bg-primary" /> */}
              <div className="px-5 py-6 sm:px-7 sm:py-7">{children}</div>
            </div>

            {footer ? <div className="mt-4 text-sm text-ink-muted">{footer}</div> : null}
          </motion.div>
        </main>
      </div>
    </div>
  );
}

/** A short, specific confirmation. Names the next action rather than apologising. */
export function AuthNotice({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div>
      <p className="flex items-start gap-2 text-sm font-medium text-primary">
        <span aria-hidden>✓</span>
        <span>{title}</span>
      </p>
      {children ? <div className="mt-3 text-sm text-ink-muted">{children}</div> : null}
    </div>
  );
}
